import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ShoppingCart, LogOut, Menu, X, LayoutDashboard, Package, User as UserIcon, Store, Home as HomeIcon, Info } from "lucide-react";

export default function Nav() {
  const { user, cart, logout, content } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const count = cart.reduce((s,x)=>s+x.qty,0);
  const isAdmin = user && (user.role === 'admin' || user.role === 'owner');

  const close = () => setOpen(false);
  const handleLogout = () => { logout(); close(); nav("/"); };

  const brand = content?.business_name || "Condor & Eagle";
  const tagline = content?.business_tagline || "OWNED & OPERATED BY NATIVE AMERICANS SINCE 2015";
  const logo = content?.logo_image || "/condor-eagle-logo.png";

  return (
    <>
      <div className="stripe-tape h-3" />
      <header className="bg-[var(--paper)] border-b-2 border-[var(--ink)] sticky top-0 z-40" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="logo-link">
            <img src={logo} alt={brand} className="w-12 h-12 object-contain border-2 border-[var(--ink)] bg-[var(--paper)] flex-shrink-0" data-testid="brand-logo"/>
            <div className="min-w-0">
              <div className="font-display text-lg leading-none truncate" data-testid="brand-name">{brand.toUpperCase()}</div>
              <div className="font-mono text-[10px] tracking-widest text-[var(--ink-2)] truncate">{tagline}</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 font-semibold text-sm uppercase tracking-wider">
            <Link to="/" data-testid="nav-home">Home</Link>
            <Link to="/shop" data-testid="nav-shop">Shop</Link>
            <Link to="/about" data-testid="nav-about">About</Link>
            <Link to="/orders" data-testid="nav-orders">My Orders</Link>
            {isAdmin && <Link to="/admin" className="text-[var(--accent)]" data-testid="nav-admin">Dashboard</Link>}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link to="/cart" className="relative p-2" data-testid="cart-link">
              <ShoppingCart size={22}/>
              {count > 0 && <span className="absolute -top-1 -right-1 bg-[var(--accent)] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold" data-testid="cart-count">{count}</span>}
            </Link>
            {/* Desktop auth state */}
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  <span className="font-mono text-xs" data-testid="user-name">{user.name}</span>
                  <button onClick={handleLogout} className="p-2 hover:text-[var(--accent)]" data-testid="logout-btn"><LogOut size={18}/></button>
                </>
              ) : (
                <Link to="/login" className="btn-primary" data-testid="login-btn">Sign In</Link>
              )}
            </div>
            {/* Mobile hamburger */}
            <button onClick={()=>setOpen(!open)} className="md:hidden p-2 border-2 border-[var(--ink)]" data-testid="mobile-menu-btn" aria-label="Menu">
              {open ? <X size={20}/> : <Menu size={20}/>}
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {open && (
          <div className="md:hidden border-t-2 border-[var(--ink)] bg-[var(--paper)]" data-testid="mobile-menu">
            <nav className="flex flex-col">
              <Link to="/" onClick={close} className="px-6 py-4 border-b border-[var(--muted)] flex items-center gap-3 font-display text-lg" data-testid="m-nav-home"><HomeIcon size={18}/> HOME</Link>
              <Link to="/shop" onClick={close} className="px-6 py-4 border-b border-[var(--muted)] flex items-center gap-3 font-display text-lg" data-testid="m-nav-shop"><Store size={18}/> SHOP</Link>
              <Link to="/about" onClick={close} className="px-6 py-4 border-b border-[var(--muted)] flex items-center gap-3 font-display text-lg" data-testid="m-nav-about"><Info size={18}/> ABOUT US</Link>
              <Link to="/orders" onClick={close} className="px-6 py-4 border-b border-[var(--muted)] flex items-center gap-3 font-display text-lg" data-testid="m-nav-orders"><Package size={18}/> MY ORDERS</Link>
              {isAdmin && (
                <Link to="/admin" onClick={close} className="px-6 py-4 border-b border-[var(--muted)] flex items-center gap-3 font-display text-lg text-[var(--accent)]" data-testid="m-nav-admin"><LayoutDashboard size={18}/> DASHBOARD</Link>
              )}
              {user ? (
                <div className="px-6 py-4 flex items-center justify-between bg-[var(--muted)]">
                  <span className="font-mono text-sm flex items-center gap-2" data-testid="m-user-name"><UserIcon size={14}/> {user.name}</span>
                  <button onClick={handleLogout} className="btn-outline py-1 px-3 text-xs" data-testid="m-logout-btn">SIGN OUT</button>
                </div>
              ) : (
                <Link to="/login" onClick={close} className="px-6 py-4 bg-[var(--ink)] text-[var(--paper)] font-display text-lg text-center" data-testid="m-login-btn">SIGN IN</Link>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
